import { useEffect, useState, useCallback } from "react";
import ProductImage from "@/components/ProductImage";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { ContactCheck, mustConfirm } from "@/components/CustomerAuth";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AddressPicker } from "@/components/AddressPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VendorRatingBadge } from "@/pages/VendorPublic";
import HeartButton from "@/components/HeartButton";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useLocation, addressLocation, openLocationPicker } from "@/contexts/LocationContext";
import { toast } from "sonner";
import { MapPin, Store, ShoppingBag, Repeat, ArrowLeft, Loader2, ShieldCheck, Star, Package, Filter } from "lucide-react";

import { logger } from "@/lib/logger";
import { payWithRazorpay } from "@/lib/razorpayCheckout";
export default function ProductDetail() {  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem } = useCart();
  const { locationParams, locationLabel, coords, addressId, setLocation, looking } = useLocation();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [freq, setFreq] = useState("");
  // Delivery address = the saved address chosen in the header location popup.
  const [pickAddress, setPickAddress] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState("");
  const [sort, setSort] = useState("distance");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [withOffers, setWithOffers] = useState(false);
  const [showAllSellers, setShowAllSellers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payMode, setPayMode] = useState("upi");   // CUS-1: auto-pay with upi | card

  const fetchProduct = useCallback(async () => {
    try {
      // Sellers are the shops that deliver to the customer's chosen location.
      const params = { ...locationParams, sort, in_stock_only: inStockOnly, verified_only: verifiedOnly };
      const { data } = await api.get(`/products/${id}`, { params });
      setProduct(data);
      const allOffers = data.offers || [];
      // Preserve the seller the user picked, using the setter so we don't need
      // `selectedOfferId` as a dep (which would refetch on every seller change).
      setSelectedOfferId(prev => {
        const stillSelected = prev && allOffers.some(o => o.id === prev);
        if (stillSelected) return prev;
        if (data.featured_offer) return data.featured_offer.id;
        if (allOffers.length) return allOffers[0].id;
        return "";
      });
    } catch (e) {
      logger.warn("Failed to fetch product:", e);
      setProduct(null);
    }
  }, [id, locationParams, sort, inStockOnly, verifiedOnly]);

  useEffect(() => { fetchProduct(); }, [fetchProduct]);

  useEffect(() => {
    if (product && !freq) {
      const freqs = product.offers?.[0]?.subscription_frequencies || [];
      if (freqs.length) setFreq(freqs[0]);
    }
  }, [product, freq]);

  const prices = (product?.offers || []).map((o) => Number(o.selling_price ?? o.price)).filter((x) => x > 0);
  const size = product ? (product.unit_value && product.unit ? `${product.unit_value} ${product.unit}` : product.size_variant) : "";
  useSeo(product ? {
    title: `${product.brand ? `${product.brand} ` : ""}${product.name}${size ? ` ${size}` : ""}: delivered from pet shops near you`,
    description: `Buy ${product.name}${size ? ` (${size})` : ""} from verified local pet shops on PawBazaar. Same-day hyperlocal delivery${prices.length ? `, from ₹${Math.min(...prices)}` : ""}.`,
    path: `/product/${id}`,
    jsonLd: { "@context": "https://schema.org", "@type": "Product", name: product.name, brand: product.brand ? { "@type": "Brand", name: product.brand } : undefined,
      category: product.category, size: size || undefined, description: product.description || undefined,
      ...(prices.length ? { offers: { "@type": "AggregateOffer", priceCurrency: "INR", lowPrice: Math.min(...prices), highPrice: Math.max(...prices),
        offerCount: prices.length, availability: "https://schema.org/InStock" } } : {}) },
  } : {});
  if (product === null) return <div className="max-w-5xl mx-auto px-6 py-10">Loading...</div>;

  const isCustomer = user && user.role === "customer";
  const selectedOffer = product.offers?.find(o => o.id === selectedOfferId);

  const requireLogin = () => {
    if (!user) {
      // Preserve the product page they were on so cart/purchase intent isn't lost.
      const next = `/product/${product.id}`;
      navigate(`/login?next=${encodeURIComponent(next)}`);
      return false;
    }
    if (user.role !== "customer") { toast.error("Sign in as a customer to purchase."); return false; }
    return true;
  };

  // Guests fill the cart too; signing in happens at checkout. Staff can't buy.
  const canAdd = () => {
    if (user && user.role !== "customer") { toast.error("Sign in as a customer to purchase."); return false; }
    if (looking) { toast("Choose where to deliver first, so we can show the shops near you."); openLocationPicker(); return false; }
    return true;
  };

  // The shop's free gift with this product (shop_bundles): added to the cart with it, at ₹0 once the cart is priced.
  const gift = (selectedOffer?.bundles || []).find((b) => b.kind === "free_gift" && qty >= (b.buy_qty || 1));
  const addBundleItem = (offer, it) => addItem({ id: `${it.catalog_product_id}::${it.offer_id}`, offer_id: it.offer_id, catalog_product_id: it.catalog_product_id,
    vendor_id: offer.vendor?.id, vendor_name: offer.vendor?.shop_name, name: it.name, price: it.price, image_url: it.image_url }, it.qty, addressId || undefined);
  const addToCart = () => {
    if (!canAdd()) return;
    if (!selectedOffer) return toast.error("Pick a seller first");
    addItem(makeCartProduct(product, selectedOffer), qty, addressId || undefined);
    if (gift) addBundleItem(selectedOffer, gift.free);
    toast.success(`Added to cart from ${selectedOffer.vendor?.shop_name}${gift ? ", with the free gift" : ""}`);
  };
  const addCombo = (offer, combo) => {
    if (!canAdd()) return;
    combo.items.forEach((it) => addBundleItem(offer, it));
    toast.success(`${combo.title} added to cart`);
  };

  const buyNow = () => {
    if (!canAdd()) return;
    if (!selectedOffer) return toast.error("Pick a seller first");
    addItem(makeCartProduct(product, selectedOffer), qty, addressId || undefined);
    if (gift) addBundleItem(selectedOffer, gift.free);
    navigate("/cart");
  };

  const subscribe = async () => {
    if (!requireLogin()) return;
    if (!selectedOffer) return toast.error("Pick a seller first");
    if (!selectedOffer.subscription_eligible) return toast.error("This seller doesn't offer subscription for this product");
    if (!addressId) { setPickAddress(true); return; }
    setLoading(true);
    const subReq = { offer_id: selectedOffer.id, frequency: freq, quantity: qty };
    let paid = false;
    try {
      // The first delivery is paid now; the server prices it and creates the
      // subscription only from a verified payment.
      const { data: cfg } = await api.get("/payments/config");
      if (cfg?.enabled) {
        const { data: rzpOrder } = await api.post("/payments/razorpay/create-order", {
          purpose: "subscription", subscription: subReq, address_id: addressId, currency: "INR",
          autopay: true, autopay_method: payMode,
        });
        const rzpCtx = await payWithRazorpay(rzpOrder, {
          user, description: "Subscription · first delivery + auto-pay",
        });
        await api.post("/payments/razorpay/verify", rzpCtx);
        paid = true;
        await api.post("/subscriptions", { ...subReq, product_id: product.id, address_id: addressId, ...rzpCtx });
      } else if (cfg?.mock_enabled) {
        await api.post("/subscriptions", { ...subReq, product_id: product.id, address_id: addressId });
      } else {
        throw new Error("Online payment is unavailable right now. Please try again later.");
      }
      toast.success(`Subscribed with ${selectedOffer.vendor?.shop_name} — first delivery being prepared!`);
      navigate("/account");
    } catch (err) {
      const msg = err?.message === "Payment cancelled" ? "Payment cancelled" : (formatApiErrorDetail(err.response?.data?.detail) || err.message);
      toast.error(paid ? `${msg} Your payment was received and will be refunded if the subscription isn't created.` : msg);
    } finally { setLoading(false); }
  };

  const activeOfferSubEligible = selectedOffer?.subscription_eligible && (selectedOffer.subscription_frequencies || []).length > 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <Button variant="ghost" onClick={() => navigate(-1)} data-testid="product-back-btn" className="mb-4 rounded-full">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-start">
        <div>
          <Card className="relative rounded-3xl border-stone-200 overflow-hidden aspect-square bg-stone-100">
            <ProductImage src={selectedOffer?.image_url || product.image_url} alt={product.name} className="w-full h-full object-cover" iconClass="h-24 w-24" />
            <HeartButton p={product} size="h-5 w-5" className="absolute top-4 right-4 !h-11 !w-11" />
            {/* The size of the pack shown, only here on the product page. */}
            {(selectedOffer?.image_url || product.image_url) && size && (
              <span data-testid="pd-size-badge" className="absolute bottom-4 right-4 rounded-full bg-stone-900 text-white font-heading font-semibold text-lg sm:text-xl px-4 py-1.5">{size}</span>
            )}
          </Card>
        </div>

        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge className="bg-terracotta/10 text-terracotta border-0 rounded-full">{product.category}</Badge>
            {product.pet_type && <Badge variant="outline" data-testid="pd-pet-type-badge" className="rounded-full border-teal/40 text-teal">For {product.pet_type}</Badge>}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">{product.name}</h1>
          <div className="mt-2 text-sm text-stone-600">
            <span className="font-semibold">{product.brand}</span> · {product.unit_value && product.unit ? `${product.unit_value} ${product.unit}` : product.size_variant}
          </div>
          {!product.featured_offer && product.num_offers > 0 && (
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-sm text-stone-500">from</span>
              <span className="font-heading text-3xl font-bold text-stoneWarm">₹{product.min_price}</span>
            </div>
          )}
          <SizePicker sizes={product.sizes} onPick={(sid) => navigate(`/product/${sid}`, { replace: true })} />
          {/* -------- Buy Box: featured vendor -------- */}
          {product.featured_offer && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">Sold by</div>
              <label
                data-testid={`buybox-featured-${product.featured_offer.id}`}
                className={`block rounded-3xl border-2 border-marigold/60 bg-marigold/5 p-5 cursor-pointer ${selectedOfferId === product.featured_offer.id ? "ring-2 ring-marigold" : ""}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <input type="radio" name="offer" checked={selectedOfferId === product.featured_offer.id} onChange={() => setSelectedOfferId(product.featured_offer.id)} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-heading text-lg font-semibold">{product.featured_offer.vendor?.shop_name}</span>
                        <OfferTags offer={product.featured_offer} />
                        {product.featured_offer.vendor?.verified && <ShieldCheck className="h-4 w-4 text-teal" />}
                        <span className="text-xs flex items-center gap-0.5 text-stone-600">
                          <Star className="h-3 w-3 fill-marigold text-marigold" /> {(product.featured_offer.vendor?.rating || 4.5).toFixed(1)}
                        </span>
                      </div>
                      <div className="text-xs text-stone-600 mt-1 flex items-center gap-2 flex-wrap">
                        <MapPin className="h-3 w-3" /> {product.featured_offer.vendor?.pincode}
                        {product.featured_offer.distance_km !== null && product.featured_offer.distance_km !== undefined && (
                          <span>· {product.featured_offer.distance_km} km {product.featured_offer.eta_min ? `· ~${product.featured_offer.eta_min} min` : ""}</span>
                        )}
                        <span>· {product.featured_offer.stock} in stock</span>
                        {product.featured_offer.subscription_eligible && (
                          <Badge className="bg-teal/15 text-teal border-0 rounded-full text-[10px]"><Repeat className="h-2.5 w-2.5 mr-0.5" /> Sub</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-heading text-3xl font-bold text-stoneWarm">₹{product.featured_offer.price}</div>
                    {product.featured_offer.deal && (
                      <div className="text-[11px] font-semibold text-terracotta" data-testid="buybox-deal">{product.featured_offer.deal.title} · {product.featured_offer.deal.label}</div>
                    )}
                    {product.featured_offer.mrp && product.featured_offer.mrp > product.featured_offer.price && (
                      <div className="mt-0.5 text-xs" data-testid={`buybox-mrp-${product.featured_offer.id}`}>
                        <span className="line-through text-stone-400 mr-1.5">₹{product.featured_offer.mrp}</span>
                        <span className="text-teal font-semibold">{Math.round((1 - product.featured_offer.price / product.featured_offer.mrp) * 100)}% OFF</span>
                      </div>
                    )}
                    <div className="text-[10px] uppercase tracking-widest text-marigold font-bold mt-1">Best match</div>
                    {product.featured_offer.vendor?.rating_summary?.count > 0 && (
                      <div className="mt-2 flex justify-end">
                        <VendorRatingBadge summary={product.featured_offer.vendor.rating_summary} compact testid={`buybox-ratings-${product.featured_offer.id}`} />
                      </div>
                    )}
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* -------- Expanded: other sellers -------- */}
          {product.other_offers && product.other_offers.length > 0 && (
            <div className="mt-3">
              <button
                data-testid="show-more-sellers-btn"
                onClick={() => setShowAllSellers(!showAllSellers)}
                className="text-sm font-semibold text-teal hover:underline flex items-center gap-2 mb-3"
              >
                <Store className="h-4 w-4" />
                {showAllSellers ? "Hide other shops" : `${product.other_offers.length} more shop${product.other_offers.length !== 1 ? "s" : ""} sell this, from ₹${Math.min(...product.other_offers.map((o) => o.price))}`}
              </button>

              {showAllSellers && (
                <>
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Filter className="h-3 w-3 text-stone-500" />
                    <Select value={sort} onValueChange={setSort}>
                      <SelectTrigger data-testid="offers-sort-select" className="h-9 w-44"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="distance">Nearest first</SelectItem>
                        <SelectItem value="price">Lowest price</SelectItem>
                        <SelectItem value="rating">Highest rated</SelectItem>
                        <SelectItem value="score">Best overall</SelectItem>
                      </SelectContent>
                    </Select>
                    <label className="flex items-center gap-1.5 text-xs text-stone-600 cursor-pointer">
                      <input type="checkbox" data-testid="offers-verified-only" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} className="h-4 w-4" />
                      Verified only
                    </label>
                    {product.other_offers.some((o) => o.bundles?.length) && (
                      <label className="flex items-center gap-1.5 text-xs text-stone-600 cursor-pointer">
                        <input type="checkbox" data-testid="offers-with-offers" checked={withOffers} onChange={(e) => setWithOffers(e.target.checked)} className="h-4 w-4" />
                        Shops with offers ({product.other_offers.filter((o) => o.bundles?.length).length})
                      </label>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {product.other_offers.filter((o) => !withOffers || o.bundles?.length).map(o => (
                      <label
                        key={o.id}
                        data-testid={`offer-card-${o.id}`}
                        className={`rounded-2xl border p-4 cursor-pointer transition-colors ${selectedOfferId === o.id ? "border-teal bg-teal/5" : "border-stone-200 hover:border-stone-300"}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2 min-w-0 flex-1">
                            <input type="radio" name="offer" checked={selectedOfferId === o.id} onChange={() => setSelectedOfferId(o.id)} className="mt-1" />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-heading font-semibold truncate">{o.vendor?.shop_name}</span>
                                {o.vendor?.verified && <ShieldCheck className="h-4 w-4 text-teal" />}
                                <OfferTags offer={o} />
                                <span className="text-xs flex items-center gap-0.5 text-stone-600">
                                  <Star className="h-3 w-3 fill-marigold text-marigold" /> {(o.vendor?.rating || 4.5).toFixed(1)}
                                </span>
                              </div>
                              <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-1 flex-wrap">
                                <MapPin className="h-3 w-3" /> {o.vendor?.pincode}
                                {o.distance_km !== null && o.distance_km !== undefined && (
                                  <span>· {o.distance_km} km {o.eta_min ? `· ~${o.eta_min} min` : ""}</span>
                                )}
                              </div>
                              <div className="mt-2 flex items-center gap-2 text-xs">
                                <Package className={`h-3 w-3 ${o.stock > 0 ? "text-emerald-600" : "text-destructive"}`} />
                                <span className={o.stock > 0 ? "text-stone-700" : "text-destructive"}>{o.stock > 0 ? `${o.stock} in stock` : "Out of stock"}</span>
                                {o.subscription_eligible && (
                                  <Badge className="bg-teal/15 text-teal border-0 rounded-full text-[10px]"><Repeat className="h-2.5 w-2.5 mr-0.5" /> Sub</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="font-heading text-xl font-bold">₹{o.price}</div>
                            {o.deal && <div className="text-[10px] font-semibold text-terracotta">{o.deal.label}</div>}
                            {o.mrp && o.mrp > o.price && (
                              <div className="text-[10px]" data-testid={`seller-mrp-${o.id}`}>
                                <span className="line-through text-stone-400 mr-1">₹{o.mrp}</span>
                                <span className="text-teal font-semibold">{Math.round((1 - o.price / o.mrp) * 100)}% OFF</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {product.num_offers === 0 && (
            <Card className="mt-6 p-6 text-center border-dashed rounded-3xl">
              <p className="text-stone-600">No shop delivers to {locationLabel} yet. Try a different location or come back soon.</p>
            </Card>
          )}

          {/* -------- Purchase flow -------- */}
          {selectedOffer && (
            <div className="mt-5">
              <Tabs defaultValue="one">
                <TabsList className="grid grid-cols-2 w-full rounded-full bg-stone-100 p-1 h-12">
                  <TabsTrigger value="one" data-testid="tab-one-time" className="rounded-full">One-time buy</TabsTrigger>
                  <TabsTrigger value="sub" data-testid="tab-subscribe" disabled={!activeOfferSubEligible} className="rounded-full">Subscribe and Save</TabsTrigger>
                </TabsList>

                <TabsContent value="one" className="mt-6 space-y-4">
                  <div className="flex items-end gap-3">
                    <div>
                      <Label htmlFor="one-qty">Quantity</Label>
                      <div className="mt-1 flex items-center rounded-full border border-stone-300 bg-white">
                        <button type="button" aria-label="Fewer" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="one-qty-dec" className="h-11 w-11 text-lg">−</button>
                        <input id="one-qty" data-testid="one-qty-input" type="number" min="1" value={qty} onChange={e => setQty(Math.max(1, +e.target.value || 1))} className="w-10 text-center font-bold bg-transparent outline-none" />
                        <button type="button" aria-label="More" onClick={() => setQty(qty + 1)} data-testid="one-qty-inc" className="h-11 w-11 text-lg">+</button>
                      </div>
                    </div>
                    <div className="pb-2 text-sm text-stone-600">Total <b className="font-heading text-lg text-stoneWarm">₹{(selectedOffer.price * qty).toFixed(0)}</b></div>
                  </div>

                  <OfferStrip offer={selectedOffer} qty={qty} onAddCombo={addCombo} />

                  <DeliveringTo label={locationLabel} detail={coords?.detail} saved={!!addressId} />

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button onClick={addToCart} data-testid="add-to-cart-btn" className="h-12 rounded-full bg-marigold text-stoneWarm hover:bg-marigold/90 font-bold flex-1">
                      <ShoppingBag className="h-4 w-4 mr-2" /> {gift ? "Add to cart, with the free gift" : "Add to cart"}
                    </Button>
                    <Button onClick={buyNow} data-testid="buy-now-btn" className="h-12 rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90 flex-1">
                      Buy now
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="sub" className="mt-6 space-y-4">
                  <Card className="p-4 rounded-2xl border-stone-200 bg-teal/5">
                    <div className="text-sm font-semibold text-teal flex items-center gap-2"><Repeat className="h-4 w-4" /> Subscribe and save 10% on renewals</div>
                    <div className="text-xs text-stone-600 mt-1">Pay for the first delivery now. Pause, skip, change or cancel anytime from your dashboard.</div>
                  </Card>
                  <AutoPayChoice value={payMode} onChange={setPayMode} />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Frequency</Label>
                      <Select value={freq} onValueChange={setFreq}>
                        <SelectTrigger data-testid="sub-frequency-select" className="h-12 mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(selectedOffer.subscription_frequencies || []).map(f => (
                            <SelectItem key={f} value={f}>{f[0].toUpperCase()+f.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Quantity per delivery</Label>
                      <Input data-testid="sub-qty-input" type="number" value={qty} onChange={e => setQty(Math.max(1, +e.target.value || 1))} className="h-12 mt-1" />
                    </div>
                  </div>

                  <DeliveringTo label={locationLabel} detail={coords?.detail} saved={!!addressId} />

                  <ContactCheck checkout />
                  <Button disabled={loading || mustConfirm(user)} onClick={subscribe} data-testid="sub-submit-btn" className="w-full h-12 rounded-full bg-marigold text-stoneWarm hover:bg-marigold/90 font-semibold">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (isCustomer && !addressId) ? "Choose delivery address to subscribe" : `Subscribe with ${selectedOffer.vendor?.shop_name}, ₹${(selectedOffer.price * qty).toFixed(0)} / ${freq}`}
                  </Button>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 max-w-3xl">
        <h2 className="font-heading text-xl font-semibold">About this product</h2>
        <p className="mt-2 text-stone-700 leading-relaxed">{product.description || "No description yet."}</p>
        <ProductRatingsStrip productId={product.id} />
      </div>

      <Dialog open={pickAddress} onOpenChange={setPickAddress}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Where should we deliver?</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-600">Subscriptions are delivered to one of your saved addresses.</p>
          {pickAddress && (
            <AddressPicker value={addressId || ""} testidPrefix="sub-addr"
              onChange={(id, a) => {
                if (!a) return;
                if (a.needs_location) return;   // the picker asks them to confirm the pin first
                setLocation(addressLocation(a));
                setPickAddress(false);
                toast.success(`Delivering to ${a.label} — tap Subscribe to continue`);
              }} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** CUS-1: subscriptions are paid by auto-pay only, approved once, now, with
 *  the first payment; each delivery is charged just before it's sent. */
function AutoPayChoice({ value, onChange }) {
  const opts = [
    ["upi", "Auto-pay with UPI", "Approve once in your UPI app (UPI AutoPay)."],
    ["card", "Auto-pay with a card", "Your card is charged for each delivery."],
  ];
  return (
    <div data-testid="sub-autopay">
      <Label>Auto-pay for later deliveries</Label>
      <div className="grid sm:grid-cols-2 gap-2 mt-1">
        {opts.map(([k, title, hint]) => (
          <button key={k} type="button" onClick={() => onChange(k)} data-testid={`sub-autopay-${k}`}
            className={`text-left rounded-xl border p-3 transition ${value === k ? "border-teal bg-teal/5 ring-1 ring-teal" : "border-stone-200 hover:border-stone-300"}`}>
            <div className="text-sm font-semibold">{title}</div>
            <div className="text-xs text-stone-600 mt-0.5">{hint}</div>
          </button>
        ))}
      </div>
      <p className="text-xs text-stone-500 mt-2">Each delivery is charged its exact amount just before it's sent. If the price ever goes up you'll hear first, and you can change or cancel before you pay more.</p>
    </div>
  );
}

/** Sizes of this product (each size is its own listing with its own sellers). */
function SizePicker({ sizes, onPick }) {
  if (!sizes || sizes.length < 2) return null;
  return (
    <div className="mt-6" data-testid="pd-sizes">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">Size</div>
      <div className="flex flex-wrap gap-2">
        {sizes.map(s => (
          <button
            key={s.id}
            type="button"
            onClick={() => !s.current && onPick(s.id)}
            data-testid={`pd-size-${s.id}`}
            aria-pressed={s.current}
            className={`min-w-[88px] rounded-2xl border-2 px-3 py-2 text-left transition-colors ${s.current ? "border-teal bg-teal/5" : "border-stone-200 hover:border-stone-300 bg-white"} ${!s.in_stock && !s.current ? "opacity-60" : ""}`}
          >
            <div className="font-semibold text-sm">{s.label}</div>
            <div className="text-xs text-stone-500">{s.min_price != null ? `from ₹${Math.round(s.min_price)}` : "Not near you"}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

/** One line under the buy buttons: where this will be delivered (the header
 *  location), with a link to change it. */
function DeliveringTo({ label, detail, saved }) {
  return (
    <div className="flex items-start gap-2 text-sm rounded-xl bg-stone-50 border border-stone-200 px-3 py-2 min-w-0" data-testid="pd-delivering-to">
      <MapPin className="h-4 w-4 text-marigold mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <span className="text-stone-500">Delivering to </span><b className="text-stoneWarm">{label}</b>
        {detail && <div className="text-xs text-stone-500 truncate">{detail}</div>}
        {!saved && <div className="text-xs text-stone-500">You'll pick the exact saved address at checkout.</div>}
      </div>
      <button type="button" onClick={openLocationPicker} className="text-xs font-semibold text-teal hover:underline shrink-0" data-testid="pd-change-location">Change</button>
    </div>
  );
}

/** Combine canonical + offer into the flat shape CartContext expects. */
/** A shop's offer tags beside its name: "Free gift: …", "Combo: …". */
function OfferTags({ offer }) {
  if (!offer?.bundles?.length) return null;
  return offer.bundles.map((b) => (
    <span key={b.id} data-testid={`offer-tag-${b.id}`}
      className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${b.kind === "combo" ? "bg-teal/10 text-teal" : "bg-marigold/15 text-stoneWarm"}`}>{b.label}</span>
  ));
}

/** The selected shop's free gift and its "Buy together" combos. */
function OfferStrip({ offer, qty, onAddCombo }) {
  const list = offer?.bundles || [];
  if (!list.length) return null;
  const rs = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  return (
    <div className="space-y-2" data-testid="offer-strip">
      {list.filter((b) => b.kind === "free_gift").map((b) => (
        <div key={b.id} className="flex items-center gap-3 rounded-2xl bg-marigold/10 border border-marigold/40 px-3 py-2.5 text-sm" data-testid={`offer-gift-${b.id}`}>
          {b.free.image_url ? <img src={b.free.image_url} alt="" className="h-10 w-10 rounded-lg object-cover bg-white" /> : <span className="h-10 w-10 rounded-lg bg-white shrink-0" />}
          <span className="flex-1"><b>Free gift:</b> {b.free.qty > 1 ? `${b.free.qty} × ` : ""}{b.free.name} (worth {rs(b.free.price * b.free.qty)})
            {b.buy_qty > 1 ? ` when you buy ${b.buy_qty}` : " with this pack"}. {b.per_customer === "once" ? "Once per customer" : "Once per order"}, till {b.end_at}.
            {qty < (b.buy_qty || 1) && <span className="block text-xs text-stone-600">Add {b.buy_qty - qty} more to get it.</span>}</span>
          {b.left != null && b.left <= 50 && <span className="text-xs font-bold text-stoneWarm">{b.left} left</span>}
        </div>
      ))}
      {list.filter((b) => b.kind === "combo").map((b) => (
        <div key={b.id} className="rounded-2xl border border-stone-200 bg-white px-3 py-3 flex flex-wrap items-center gap-3" data-testid={`offer-combo-${b.id}`}>
          <span className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-teal text-white">Buy together</span>
          <div className="min-w-0 flex-1 basis-48">
            <div className="font-semibold text-sm">{b.title}</div>
            <div className="text-xs text-stone-600">{b.items.map((i) => `${i.qty > 1 ? `${i.qty} × ` : ""}${i.name}`).join(" + ")}</div>
          </div>
          <div className="text-right text-sm"><b className="text-base">{rs(b.price)}</b> <s className="text-stone-500">{rs(b.normal_price)}</s>
            <div className="text-xs font-bold text-teal">Save {rs(b.saving)}</div></div>
          <Button variant="outline" className="rounded-full" onClick={() => onAddCombo(offer, b)} data-testid={`offer-combo-add-${b.id}`}>Add all {b.items.length}</Button>
        </div>
      ))}
    </div>
  );
}

function makeCartProduct(canonical, offer) {
  return {
    id: `${canonical.id}::${offer.id}`,        // unique per (product, vendor) combo
    offer_id: offer.id,
    catalog_product_id: canonical.id,
    vendor_id: offer.vendor?.id,
    vendor_name: offer.vendor?.shop_name,
    name: canonical.name,
    price: offer.price,
    image_url: offer.image_url || canonical.image_url,
  };
}

// Iter-8: Public 3-D ratings strip for canonical products (aggregated across all sellers of the same catalog product)
function ProductRatingsStrip({ productId }) {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/products/${productId}/reviews`)
      .then(({ data }) => alive && setData(data))
      .catch((e) => { logger.warn("Failed to load product reviews:", e); });
    return () => { alive = false; };
  }, [productId]);
  if (!data || !data.summary?.count) {
    return (
      <div className="mt-5 text-xs text-stone-500" data-testid="product-ratings-none">
        No ratings yet — be the first to review after your delivery!
      </div>
    );
  }
  const s = data.summary;
  const reviews = data.reviews || [];
  return (
    <div className="mt-6 rounded-2xl border border-marigold/30 bg-marigold/5 p-4" data-testid="product-ratings-strip">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-marigold flex items-center gap-1">
          <Star className="h-3 w-3 fill-marigold" /> Verified customer ratings
        </div>
        <div className="text-xs text-stone-500">· {s.count} review{s.count === 1 ? "" : "s"}</div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <RatingBlock label="Product" value={s.product_quality_avg} testid="product-rating-pq" />
        <RatingBlock label="Vendor" value={s.vendor_experience_avg} testid="product-rating-ve" />
        <RatingBlock label="Delivery" value={s.delivery_experience_avg} testid="product-rating-de" />
      </div>
      {reviews.length > 0 && (
        <div className="mt-4">
          <button
            className="text-xs text-marigold font-medium hover:underline"
            onClick={() => setShowAll(v => !v)}
            data-testid="product-ratings-toggle"
          >
            {showAll ? "Hide reviews" : `See ${Math.min(reviews.length, 3)} recent review${reviews.length === 1 ? "" : "s"}`}
          </button>
          {showAll && (
            <div className="mt-3 space-y-2" data-testid="product-ratings-list">
              {reviews.slice(0, 5).map(r => (
                <div key={r.id} className="text-xs text-stone-700 border-t border-marigold/20 pt-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{r.customer_name || "Anonymous"}</span>
                    <span className="text-stone-500">· {r.vendor_name}</span>
                    <span className="ml-auto text-marigold font-medium">Product {r.product_quality}/5</span>
                  </div>
                  {r.comment && <p className="mt-1 italic text-stone-600">"{r.comment}"</p>}
                  {r.shop_reply && <div className="mt-2 rounded-lg bg-stone-50 px-3 py-2 text-xs text-stone-700"><b>Reply from {r.shop_reply.by_name || "the shop"}:</b> {r.shop_reply.text}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RatingBlock({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className="mt-1 font-heading text-2xl font-bold text-stoneWarm">
        {value != null ? value.toFixed(1) : "—"}<span className="text-sm text-stone-400 font-normal"> /5</span>
      </div>
      <div className="flex justify-center gap-0.5 mt-1">
        {[1,2,3,4,5].map(n => (
          <Star key={n} className={`h-3 w-3 ${value != null && n <= Math.round(value) ? "text-marigold fill-marigold" : "text-stone-300"}`} />
        ))}
      </div>
    </div>
  );
}

